import { computed, ref, type Ref } from 'fine-jsx'
import { uid } from 'uid'
import { Renderer as EffectRenderer } from 'webgl-effects'

import type * as pub from '#core'
import type * as Schema from '#schema'
import type { Size } from 'shared/types'
import { IS_FIREFOX } from 'shared/user-agent.ts'
import { useElementSize, useFullscreen } from 'shared/utils/composables.ts'
import { getWebgl2Context } from 'shared/utils/images.ts'
import { Rational } from 'shared/utils/math'

import { useClipDragResize } from './components/interactions/clip-drag-resize.ts'
import { useTrackDropzone } from './components/interactions/track-dropzone.ts'
import type { AssetBin } from './constants.ts'
import { EditDocument } from './document-views/edit/edit-document.ts'
import type { EditView } from './document-views/edit/edit-nodes.ts'
import { PlaybackDocument } from './document-views/playback/playback-document.ts'
import { RenderDocument } from './document-views/render/index.ts'
import { Document } from './document.ts'
import { EDITOR_SELECTION_ACTIONS_BY_ID } from './editor-actions.ts'
import type { NodeDeleteEvent } from './events.ts'
import { TimelineZoom } from './utils.ts'

const MOBILE_SCREEN_CUTOFF_PX = 1000

export class VideoEditor implements pub.VideoEditor {
  readonly #uid = uid()

  readonly doc: EditDocument
  readonly _editor = this
  readonly sync?: pub.VideoEditorDocumentSync

  readonly #selection = ref<pub.AnyClip | pub.GapSelection>()
  readonly #linkedSelection = computed(() => {
    const { selection } = this
    return selection?.isNode && selection.isClip() ? (selection.link?.nodes ?? []) : []
  })
  readonly _timelineContainer = ref<HTMLElement>()
  readonly _timelineSize = useElementSize(this._timelineContainer)
  readonly _viewportContainer = ref<HTMLElement>()
  readonly _viewportContainerSize = useElementSize(this._viewportContainer)
  readonly _fullscreen = useFullscreen(this._viewportContainer)
  readonly _viewportSize: Ref<Size>
  readonly _viewport = ref<HTMLElement>()
  readonly _workspaceContainer = ref<HTMLElement>()
  readonly _workspaceSize = useElementSize(this._workspaceContainer)
  readonly _zoom = computed(() => this.viewportSize.width / this.doc.resolution.width)

  readonly timelineZoom = new TimelineZoom(this._workspaceSize, () => this.doc)

  get drag() {
    return this.doc.clipDrag
  }
  get resize() {
    return this.doc.clipResize
  }

  effectRenderer: EffectRenderer
  playback: PlaybackDocument

  canvas = document.createElement('canvas')
  isDisposed = false
  readonly #ownsDoc: boolean

  get isPaused(): boolean {
    return this.playback.isPaused
  }

  get currentTime(): number {
    return this.doc.currentTime
  }

  _isMobileWorkspace = computed(() => {
    const { width } = this._workspaceSize.value
    return width !== 0 && width < MOBILE_SCREEN_CUTOFF_PX
  })

  get isMobileWorkspace(): boolean {
    return this._isMobileWorkspace.value
  }

  readonly #effects = computed(
    () =>
      new Map(
        Array.from(this.doc.assets.values())
          .filter((asset) => asset.type === 'asset:effect:video')
          .map((effect) => [effect.id, effect]),
      ),
  )
  get effects(): Map<string, pub.VideoEffectAsset> {
    return this.#effects.value
  }

  readonly #showStats = ref(false)
  get _showStats(): boolean {
    return this.#showStats.value
  }
  set _showStats(value) {
    this.#showStats.value = value
  }

  readonly #activeAssetBin = ref<AssetBin>(null)
  get activeAssetBin(): AssetBin {
    return this.#activeAssetBin.value
  }
  set activeAssetBin(value: AssetBin) {
    this.#activeAssetBin.value = value
  }

  readonly #exportProgress = ref(-1)
  get exportProgress(): number {
    return this.#exportProgress.value
  }

  get selection(): EditView.AnyTrackChild | pub.GapSelection | undefined {
    const selection = this.#selection.value
    return selection?.isNode ? this.doc._getNode(selection) : selection
  }
  get linkedSelection(): EditView.AnyClip[] {
    return this.#linkedSelection.value
  }

  get tracks(): pub.AnyTrack[] {
    return this.doc.timeline.children
  }

  get viewportContainerSize(): Size {
    return this._viewportContainerSize.value
  }
  get viewportSize(): Size {
    return this._viewportSize.value
  }

  get canvasZoom(): number {
    return this._zoom.value
  }

  constructor(options: { sync?: pub.VideoEditorDocumentSync; assets?: pub.VideoEditorAssetStore } = {}) {
    const { sync, assets } = options
    const doc = new EditDocument(sync?.doc ?? new Document({ assets }))

    this.#ownsDoc = !sync
    this.sync = sync
    this.doc = doc

    useClipDragResize(this)
    useTrackDropzone(this)

    const renderView = new RenderDocument({
      doc,
      applyVideoRotation: IS_FIREFOX,
      gl: getWebgl2Context(this.canvas, { stencil: true }),
    })
    this.playback = new PlaybackDocument({ renderView })

    this.effectRenderer = new EffectRenderer({ gl: getWebgl2Context(document.createElement('canvas')) })

    this.doc.on('node:delete', (event: NodeDeleteEvent) => {
      const { selection } = this
      if (!selection) return
      const deletedId = event.node.id

      if (
        // unselect the deleted node
        (selection.isNode && deletedId === selection.id) ||
        // unselect the gaps around the deleted node
        !((selection.isNode && selection.prev?.id === deletedId) || selection.next?.id === deletedId)
      )
        this.select(undefined)
    })

    this.doc.on('canvas:pointerdown', ({ node }) => this.select(node))

    this._viewportSize = useElementSize(this.canvas)
  }

  generateId(): string {
    const { sync } = this
    return sync ? sync.generateId() : uid()
  }

  getPartId(part: string): string {
    return `${part}-${this.#uid}`
  }

  importJson(content: Schema.SerializedDocument): void {
    this.doc.importFromJson(content)
  }

  async createMediaAsset(source: string | Blob): Promise<pub.MediaAsset | pub.ImageAsset> {
    return await this.doc.assets.createMediaAsset(source)
  }

  addMediaClip(asset: pub.MediaAsset | pub.ImageAsset): pub.AnyClip {
    return this._transact(() => {
      const { type } = asset
      const newClipDurationS = type === 'asset:media:av' ? asset.duration : 2
      const track = this.getOrCreateTrackForMedia(asset)

      const init: Schema.AnyClip = {
        id: this.generateId(),
        type: type === 'asset:media:image' ? 'clip:image' : `clip:${track.isVideo() ? 'video' : 'audio'}`,
        name: asset.name,
        color: asset.color,
        mediaRef: { id: asset.id, type: asset.type },
        sourceStart: Rational.fromDecimal(0, this.doc.frameRate),
        duration: Rational.fromDecimal(newClipDurationS, this.doc.frameRate),
      }

      const clip = this.doc.createNode(init)
      clip.move({ parentId: track.id, index: track.children.length })

      // Create a linked audio clip for video clips
      // TODO: improve linking UX
      if ('audio' in asset && track.isVideo() && track.link) {
        const linkedAudioTrack = track.link.nodes.find((n) => n.type === 'track:audio')
        if (linkedAudioTrack) {
          const audioClip = this.doc.createNode({
            ...init,
            id: this.generateId(),
            type: 'clip:audio',
            mediaRef: asset.audio ? init.mediaRef : undefined,
          })
          audioClip.move({ parentId: linkedAudioTrack.id, index: track.children.length })
          this.doc.createLink({ id: this.generateId(), nodes: [clip, audioClip] })
        }
      }

      return clip
    })
  }

  replaceClipAsset(asset: pub.MediaAsset): void {
    const clip = this.#selection.value
    if (!clip?.isNode || !clip.isClip()) return

    this._transact(() => {
      clip.duration = Rational.min(Rational.fromDecimal(asset.duration, clip.duration.rate), clip.duration)
      clip.mediaRef = { id: asset.id, type: asset.type }
    })
  }

  splitClip(clip: pub.AnyClip, time: number): [pub.AnyClip, pub.AnyClip] | undefined {
    if (!clip.parent) return

    const { parent, gap: startGap } = clip
    const clipTime = clip.timeRational
    const { frameRate } = this.doc
    const endGap = clip.next?.gap

    const splitTimeRational = Rational.fromDecimal(time, frameRate)

    if (clipTime.end.toRate(frameRate).subtract(splitTimeRational).value < 1) return

    const delta = splitTimeRational.subtract(clipTime.start.toRate(frameRate))

    if (delta.value < 1) return

    const clipJson = clip.toJSON()
    const startAnimations: Schema.AnyClip['animations'] = {}
    const endAnimations: Schema.AnyClip['animations'] = {}

    clip.animationMap.forEach((animation, key) => {
      const keyframeInits = clipJson.animations?.[key]?.keyframes ?? []
      const split = animation.splitAt(delta)
      if (!split) return

      const start = keyframeInits.slice(0, split.sliceToIndex)
      const end = keyframeInits.slice(split.sliceToIndex)
      startAnimations[key] = { keyframes: start }
      endAnimations[key] = { keyframes: end }

      const splitToInit = {
        ...keyframeInits[split.sliceTo ? split.sliceToIndex : split.sliceToIndex - 1],
        value: split.splitValue,
      }

      start.push({
        ...splitToInit,
        dt: delta.subtract(split.sliceTo?.prev?.time ?? Rational.ZERO),
      })
      if (end.at(0)) end[0].dt = split.sliceTo!.time.subtract(delta)

      if (!split.isOnKeyframe) {
        end.unshift({ ...splitToInit, dt: Rational.ZERO })
      }
    })

    const startClip = this.doc.createNode({
      ...clipJson,
      id: this.generateId(),
      transition: undefined,
      duration: delta,
      keyframes: startAnimations,
    })
    const endClip = this.doc.createNode({
      ...clipJson,
      id: this.generateId(),
      sourceStart: clipTime.source.add(delta),
      duration: clipTime.duration.subtract(delta),
      keyframes: endAnimations,
    })

    startClip.move({ parentId: parent.id, index: clip.index })
    endClip.move({ parentId: parent.id, index: clip.index + 1 })

    startClip.gap = startGap
    if (endClip.next && endGap) endClip.next.gap = endGap

    this.#select(startClip, false)
    clip.delete()

    return [startClip, endClip]
  }

  getTrackForMedia(asset: {
    video?: boolean | (pub.MediaAsset | pub.ImageAsset)['video']
  }): pub.AnyTrack | undefined {
    const hasVideo = (asset.video ?? false) !== false

    // add to the last track of the correct type
    return [...this.tracks].reverse().find(
      (t) =>
        t.isVideo() === hasVideo &&
        // if the asset is video, the track must be linked to an audio track
        // TODO: consider relaxing this requirement when improving linking UX
        hasVideo === !!t.link,
    )
  }
  getOrCreateTrackForMedia(asset: {
    video?: (pub.MediaAsset | pub.ImageAsset)['video'] | boolean
  }): pub.AnyTrack {
    const found = this.getTrackForMedia(asset)
    if (found) return found

    const newTrack = this.addTrack((asset.video ?? false) === false ? 'audio' : 'video')

    // always create and linked audio tracks for video tracks
    // TODO: reconsider when improving linking UX
    if (newTrack.isVideo()) {
      const audioTrack = this.addTrack('audio')
      this.doc.createLink({ id: this.generateId(), nodes: [newTrack, audioTrack] })
    }

    return newTrack
  }

  addTrack(trackType: 'video' | 'audio'): pub.AnyTrack {
    const { doc } = this

    return this._transact(() => {
      const track = doc.createNode({ id: this.generateId(), type: `track:${trackType}` })
      track.move({ parentId: doc.timeline.id, index: track.isVideo() ? 0 : doc.timeline.count })
      return track
    })
  }

  play(): void {
    this.playback.play()
  }
  pause(): void {
    this.playback.pause()
  }
  seekTo(time: number): void {
    this.doc.seekTo(time)
  }

  select(item: pub.AnyClip | pub.GapSelection | undefined, seek = item?.isNode ?? true): void {
    this.#select(item, seek)
  }
  #select(item: pub.AnyClip | pub.GapSelection | undefined, seek: boolean): void {
    const { doc } = this

    if (!item) {
      this.#selection.value = undefined
      return
    }

    const node = doc._getNode(item.isNode ? item : item.node)
    this.#selection.value = item.isNode
      ? node
      : {
          node,
          isNode: false,
          get prev() {
            return node.prev
          },
          get next() {
            return node.next
          },
        }

    if (item.isNode && seek) {
      const { start, end } = node.time

      if (doc.currentTime < start) doc.seekTo(start)
      else if (doc.currentTime >= end) doc.seekTo(end - 1 / doc.frameRate)
    }
  }

  _transact<T>(fn: () => T): T {
    return this.sync ? this.sync.transact(fn) : fn()
  }

  action(id: string) {
    this._transact(() => EDITOR_SELECTION_ACTIONS_BY_ID[id].exec(this))
  }

  secondsToPixels(time: number): number {
    return this.timelineZoom.secondsToPixels(time)
  }

  pixelsToSeconds(offset: number): number {
    return this.timelineZoom.pixelsToSeconds(offset)
  }

  async export(options?: { signal?: AbortSignal }): Promise<Blob> {
    const onProgress = (value: number): void => void (this.#exportProgress.value = value)

    this.playback.pause()
    onProgress(0)

    try {
      const { ExportDocument } = await import('./document-views/export/exporter-document.ts')
      const exporter = new ExportDocument({ doc: this.doc, renderOptions: this.playback.renderView })
      let result

      await this.playback
        .withoutRendering(async () => {
          const blob = await exporter.start({ onProgress, signal: options?.signal })
          result = blob
        })
        .finally(() => exporter.dispose())

      return result!
    } finally {
      onProgress(-1)
    }
  }

  dispose(): void {
    if (this.isDisposed) return
    this.isDisposed = true

    this.playback.renderView.dispose()
    this.playback.dispose()
    if (this.#ownsDoc) this.doc.dispose()
  }

  [Symbol.dispose](): void {
    this.dispose()
  }
}
